/**
 * OTRACK-SUBSYSTEM — object-snap tracking alignment-path guide lines.
 *
 * World-space dashed lines that visualise the active OTRACK alignment
 * path(s) from acquired object-snap points. Sibling of {@link ./polarGuide},
 * but a POOL of lines (not one) — AutoCAD genuinely renders TWO
 * simultaneous dashed lines during combined tracking (two acquired points'
 * paths crossing near the cursor), so a single-line handle would silently
 * drop the second (CLAUDE.md ПРИОРИТЕТ #1: no single-instance in place of a
 * real multi-instance case).
 *
 * A small fixed-size pool (not a THREE.Line per acquired point × angle) is
 * enough: `resolveOtrackAlignment` only ever returns 1 or 2 engaged rays at
 * a time (single path, or one combined intersection), so the pool only
 * needs to render whatever the caller passes for THIS frame.
 */

import * as THREE from "three";

/** Generous cap — real engagement is 1 or 2; sized for headroom, not a hard AutoCAD limit. */
export const OTRACK_GUIDE_POOL_SIZE = 8;

const OTRACK_GUIDE_COLOR = "#3fa9ff"; // sky-blue — distinct from POLAR's teal-green and OSNAP's yellow.

export interface OtrackGuideHandle {
  /** Group containing every pooled line; caller adds THIS to the scene. */
  group: THREE.Group;
  /** Show exactly `segments.length` lines at the given endpoints; hides the rest of the pool. */
  setSegments(segments: ReadonlyArray<{ from: { x: number; y: number }; to: { x: number; y: number } }>): void;
  setHidden(): void;
  dispose(): void;
}

function createOneLine(material: THREE.LineDashedMaterial): THREE.Line {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([0, 0, 0, 0, 0, 0]);
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const line = new THREE.Line(geometry, material);
  line.userData = { kind: "otrack-guide" };
  line.visible = false;
  line.renderOrder = 50;
  line.frustumCulled = false;
  return line;
}

export function createOtrackGuide(poolSize = OTRACK_GUIDE_POOL_SIZE): OtrackGuideHandle {
  const group = new THREE.Group();
  group.userData = { kind: "otrack-guide-pool" };

  const material = new THREE.LineDashedMaterial({
    color: new THREE.Color(OTRACK_GUIDE_COLOR),
    dashSize: 6,
    gapSize: 4,
    transparent: true,
    opacity: 0.6,
    depthTest: false,
    depthWrite: false,
  });

  const lines: THREE.Line[] = [];
  for (let i = 0; i < poolSize; i++) {
    const line = createOneLine(material);
    group.add(line);
    lines.push(line);
  }

  return {
    group,
    setSegments(segments) {
      const n = Math.min(segments.length, lines.length);
      for (let i = 0; i < n; i++) {
        const line = lines[i]!;
        const seg = segments[i]!;
        const positionAttr = line.geometry.attributes.position as THREE.BufferAttribute;
        const arr = positionAttr.array as Float32Array;
        arr[0] = seg.from.x;
        arr[1] = seg.from.y;
        arr[2] = 0;
        arr[3] = seg.to.x;
        arr[4] = seg.to.y;
        arr[5] = 0;
        positionAttr.needsUpdate = true;
        line.computeLineDistances();
        line.visible = true;
      }
      for (let i = n; i < lines.length; i++) {
        lines[i]!.visible = false;
      }
    },
    setHidden() {
      for (const line of lines) line.visible = false;
    },
    dispose() {
      for (const line of lines) line.geometry.dispose();
      material.dispose();
    },
  };
}
